import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import Calendar from "components/crm/ui/Calendar";
import Chip from "components/crm/ui/Chip";
import Label from "components/crm/ui/Label";
import TextField from "components/crm/ui/TextField";
import {
  capitalize,
  fromIsoDay,
  isoDay,
  startOfDay,
  useCrmLocale,
} from "components/crm/ui/crmTime";
import useText from "hooks/useText";
import {
  DEFAULT_CALL_RULES,
  callMoments,
  clock,
  clockInWindow,
  longDay,
  parseClock,
  resolveWhen,
  weekdayName,
  windowClock,
} from "pages/prospection/callTime";

const MOMENT_KEYS = { hour: "inOneHour", afternoon: "afternoon" };

// Top of the nearest scrolling ancestor: what clips a popover opened above.
const clipTop = (node) => {
  for (let el = node.parentElement; el; el = el.parentElement) {
    if (/(auto|scroll)/.test(getComputedStyle(el).overflowY)) {
      return el.getBoundingClientRect().top;
    }
  }
  return 0;
};

// "When to call back?": same chips for "to call back" and "no answer".
// value: { choice: 'hour' | 'afternoon' | 'tomorrow' | 'other', day: 'YYYY-MM-DD' | null, time }
// callWindow: { start, end } ('HH:MM', Martinique): no moment outside it.
const WhenPicker = ({
  id,
  title,
  hint,
  value,
  onChange,
  now,
  callWindow = DEFAULT_CALL_RULES.callWindow,
}) => {
  const { text } = useText("prospection");
  const locale = useCrmLocale();
  const moments = callMoments(now, callWindow);
  const [open, setOpen] = useState(false);
  const [up, setUp] = useState(true);
  const box = useRef(null);
  const popover = useRef(null);

  // Above like the prototype, below when the scrolling sheet would cut it off.
  useLayoutEffect(() => {
    if (!open) {
      if (!up) setUp(true);
      return;
    }
    const node = popover.current;
    if (!node) return;
    if (up && node.getBoundingClientRect().top < clipTop(node)) setUp(false);
    if (!up) node.scrollIntoView?.({ block: "nearest" });
  }, [open, up]);

  useEffect(() => {
    const close = (event) => {
      if (box.current && !box.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  // A moment no longer offered (afternoon after 2 pm) falls back to the last one.
  const stale =
    value.choice !== "other" &&
    !moments.some((moment) => moment.id === value.choice);
  useEffect(() => {
    if (stale) onChange({ ...value, choice: moments[moments.length - 1].id });
  });

  const label = (moment) => {
    const time = clock(moment.at);
    if (moment.id === "tomorrow" && !moment.nextDay) {
      return text("when.weekday", {
        day: capitalize(weekdayName(moment.at, locale)),
        time,
      });
    }
    return text(`when.${MOMENT_KEYS[moment.id] ?? "tomorrow"}`, { time });
  };

  const at = resolveWhen(value, moments, now, callWindow);
  const isOther = value.choice === "other";
  const typedClock = parseClock(value.time);
  const timeValid = !value.time || !!typedClock;
  const outsideWindow =
    !!typedClock && !clockInWindow(typedClock, callWindow);
  const inPast = isOther && value.day && !!typedClock && !outsideWindow && !at;
  let timeError = null;
  if (!timeValid) timeError = text("when.timeInvalid");
  else if (outsideWindow) {
    timeError = text("when.timeOutsideWindow", {
      start: windowClock(callWindow.start),
      end: windowClock(callWindow.end),
    });
  } else if (inPast) timeError = text("when.timePast");

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-semibold leading-5 text-ink">{title}</p>
      <div className="flex flex-wrap gap-2" data-enter-confirms="">
        {moments.map((moment) => (
          <Chip
            key={moment.id}
            selected={value.choice === moment.id}
            onClick={() => onChange({ ...value, choice: moment.id })}
          >
            {label(moment)}
          </Chip>
        ))}
        <Chip
          selected={isOther}
          onClick={() => {
            onChange({ ...value, choice: "other" });
            setOpen(true);
          }}
        >
          {text("when.other")}
        </Chip>
      </div>
      {isOther && (
        <div className="flex gap-3">
          <div ref={box} className="relative flex-1">
            <div className="flex flex-col gap-2">
              <Label htmlFor={`${id}-day`}>{text("when.dateLabel")}</Label>
              <button
                id={`${id}-day`}
                type="button"
                onClick={() => setOpen((current) => !current)}
                className={`flex items-center gap-2 rounded-lg border bg-white p-3 text-left text-sm font-medium leading-5 transition-colors ${
                  open ? "border-primary" : "border-lineStrong hover:border-faint"
                } ${value.day ? "text-ink" : "text-faint"}`}
              >
                <CalendarIcon size={18} className="shrink-0 text-faint" />
                {value.day
                  ? capitalize(longDay(fromIsoDay(value.day), locale))
                  : text("when.pickDay")}
              </button>
            </div>
            {open && (
              <div
                ref={popover}
                className={`absolute left-0 z-20 ${
                  up ? "bottom-full mb-2" : "top-full mt-2"
                }`}
              >
                <Calendar
                  value={value.day ? fromIsoDay(value.day) : undefined}
                  minDate={startOfDay(now)}
                  onChange={(day) => {
                    onChange({ ...value, day: isoDay(day) });
                    setOpen(false);
                  }}
                />
              </div>
            )}
          </div>
          <TextField
            className="w-40"
            id={`${id}-time`}
            type="time"
            label={text("when.timeLabel")}
            value={value.time}
            onChange={(event) =>
              onChange({ ...value, time: event.target.value })
            }
            error={timeError}
          />
        </div>
      )}
      <p className="text-xs leading-4 text-muted">
        {isOther && at
          ? text("when.otherHint", {
              day: longDay(at, locale),
              time: clock(at),
            })
          : hint}
      </p>
    </div>
  );
};

export default WhenPicker;
